import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSyncJobCostCurrentSummary,
  getSyncJobCostPhases,
  getSyncJobCostsCurrent,
  getSyncJobCostMonthlySummary,
  getCostCodeNotes,
  addCostCodeNote,
  getArJobCollections,
  getJobHoursCurrent,
  getJobHoursMonthly,
  getArInvoicedMonthly,
} from '../api';
import LoadingState from './LoadingState';
import StateScreen from './ui/StateScreen';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { PsrExportMenu } from './jobcost/PsrExportMenu';
import {
  fmtPct, fmtDate, JcIcon, HealthBadge, KpiCards, MonthlyChart, MonthlyLegend, MonthlyNote, PhaseTable,
  BudgetBar, BillingBar, SpentRing, applyBudgetAssumption, buildPhases, buildMonthly, buildFinancials,
} from './jobcost/JobCostShared';

/* ═══════════════════════════════════════════════════════════════════════════
   Job Costs tab (project-detail) — warm-cream + sage redesign
   Single-project version of the Job Costs dashboard's per-job content, sharing
   the same primitives. Token-only colors. Visual reskin — CE-sync API, SSE,
   and assumed-at-budget logic unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const signTone = (v) => (v >= 0 ? 'var(--success)' : 'var(--danger)');

const normalizeNote = (n) => ({
  // GET/POST /api/cost-code-notes send the author as user_name.
  author: n.user_name || n.author || n.created_by || n.changed_by || n.user || '—',
  text: n.note || n.text || '',
  date: n.created_at || n.date || n.changed_at || null,
});

const StripCell = ({ label, children, flex = '0 0 auto', minWidth = 0 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex, minWidth, padding: '2px 0' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{label}</div>
    {children}
  </div>
);
const StripDivider = () => <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '4px 0', flex: '0 0 auto' }} />;

const HeaderStrip = ({ fin, onRefresh, exportProject }) => {
  const gmDelta = fin.actual_gm_pct - fin.gm_target;
  return (
    <section style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', padding: '16px 18px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <StripCell label="Budget Utilization" flex="1 1 280px" minWidth={240}>
        <BudgetBar budget={fin.budget} cost={fin.actual_cost} committed={fin.committed} />
      </StripCell>
      <StripDivider />
      <StripCell label="Billing % Complete" flex="1 1 200px" minWidth={170}>
        <BillingBar pct={fin.billing_pct} billed={fin.billed} contract={fin.contract_value} />
      </StripCell>
      <StripDivider />
      <StripCell label="Actual GM %">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span className="mono" style={{ fontSize: 22.5, fontWeight: 800, color: signTone(fin.actual_gm), fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em', lineHeight: 1 }}>{fmtPct(fin.actual_gm_pct * 100)}</span>
          <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
            {(gmDelta >= 0 ? '+' : '') + fmtPct(gmDelta * 100)} vs {fmtPct(fin.gm_target * 100)} target
          </span>
        </div>
      </StripCell>
      <StripDivider />
      <StripCell label="% Spent">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SpentRing pct={fin.pctSpent} size={58} />
          <HealthBadge pct={fin.pctSpent} />
        </div>
      </StripCell>
      <div style={{ flex: 1, minWidth: 8 }} />
      {/* PDF report or Excel workbook (PSR #14) — the same menu as the job pop-up on Finance → Job Costs. */}
      <div style={{ alignSelf: 'flex-start' }}><PsrExportMenu project={exportProject} /></div>
      <button type="button" className="j-refresh" onClick={onRefresh} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 13px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', alignSelf: 'flex-start' }}>
        <JcIcon name="refresh" size={13} /> Refresh
      </button>
    </section>
  );
};

const JobCostTab = ({ project, endDate, onOpenSov }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rawPhases, setRawPhases] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [notesCache, setNotesCache] = useState([]);
  const [collections, setCollections] = useState(undefined);
  const [hours, setHours] = useState(undefined); // labor hours rows; null = unavailable
  const [hoursMonthly, setHoursMonthly] = useState(null);
  const [invoicedMonthly, setInvoicedMonthly] = useState(null);
  const phasesRef = useRef(null);
  const search = usePageSearch('Search phases and cost codes…');

  const ceJobId = project?.ce_job_id || project?.project_number || null;

  const loadData = useCallback(async () => {
    if (!ceJobId) { setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const [summaryData, phaseData, rawItems, trendData, notes, colData, hoursData, hoursTrend, invoicedTrend] = await Promise.all([
        getSyncJobCostCurrentSummary({ job_id: ceJobId }),
        getSyncJobCostPhases({ job_id: ceJobId, source: 'current' }),
        getSyncJobCostsCurrent(ceJobId),
        getSyncJobCostMonthlySummary(ceJobId),
        getCostCodeNotes(ceJobId).catch(() => []),
        getArJobCollections(ceJobId).catch(() => null),
        getJobHoursCurrent(ceJobId).catch(() => null),
        getJobHoursMonthly(ceJobId).catch(() => null),
        getArInvoicedMonthly(ceJobId).catch(() => null),
      ]);
      setSummary(summaryData?.[0] || null);
      setRawPhases(phaseData || []);
      setLineItems(applyBudgetAssumption(rawItems || [], ceJobId));
      setMonthly(trendData || []);
      setNotesCache(notes || []);
      setCollections(colData);
      setHours(hoursData);
      setHoursMonthly(hoursTrend);
      setInvoicedMonthly(invoicedTrend);
    } catch (err) {
      console.error('Failed to load job cost data:', err);
      setError(err.message || 'Failed to load job cost data');
    } finally {
      setLoading(false);
    }
  }, [ceJobId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!ceJobId) return undefined;
    const sseBase = process.env.NODE_ENV === 'production' ? '/api/sync/events' : 'http://localhost:5001/api/sync/events';
    const es = new EventSource(sseBase);
    es.addEventListener('sync-complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (!data.job_id || data.job_id === ceJobId) loadData();
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    });
    return () => es.close();
  }, [ceJobId, loadData]);

  const getNotes = (phaseNo, code) =>
    notesCache
      .filter((n) => String(n.phase) === String(phaseNo) && String(n.category || '') === String(code || ''))
      .map(normalizeNote);

  const addNote = async (phaseNo, code, text) => {
    const created = await addCostCodeNote({ job_id: ceJobId, phase: phaseNo, category: code || '', note: text });
    setNotesCache((prev) => [...prev, created]);
  };

  if (loading) return <LoadingState message="Loading cost data…" padding="80px 20px" />;

  if (!ceJobId) {
    return (
      <StateScreen
        kind="empty"
        title="This project isn’t linked to a ComputerEase job"
        body="Job costs sync from ComputerEase once this project's number matches a CE job ID (e.g. CHICO-D-02). Set it to see budget, cost, and phase-level detail here."
      />
    );
  }
  if (error) {
    return (
      <StateScreen
        kind="server-error"
        title="Couldn’t load job costs"
        body="Something went wrong fetching the ComputerEase cost sync for this project. Check your connection and try again."
        primary={{ label: 'Retry', onClick: loadData }}
      />
    );
  }
  if (!summary) {
    return (
      <StateScreen
        kind="empty"
        title="No cost data available yet"
        body={`No synced cost data found for job ${ceJobId}. Run a CE Agent pull to populate budgets, costs, and phases.`}
      />
    );
  }

  const fin = buildFinancials(summary, project);
  const phases = buildPhases(rawPhases, lineItems);
  // The top bar's search (lib/pageSearch) filters the Phase Breakdown: a cost code shows when it or
  // its phase matches. A phase left with only some of its codes is summed from those (as buildPhases
  // sums a phase), so its row and the table's Total add up to what's shown. The KPI cards keep the job.
  const shownPhases = phases.flatMap((ph) => {
    const codes = ph.codes.filter((cd) => matchesSearch(search, ph.name, `Phase ${ph.no}`, cd.cat, cd.code, cd.assumed ? 'assumed' : null));
    if (codes.length === ph.codes.length) return codes.length || matchesSearch(search, ph.name, `Phase ${ph.no}`) ? [ph] : [];
    if (!codes.length) return [];
    const sum = (k) => codes.reduce((s, c) => s + c[k], 0);
    return [{ ...ph, codes, budget: sum('budget'), actual_cost: sum('actual_cost'), committed: sum('committed'), over_under: sum('actual_cost') - sum('budget') }];
  });
  const monthlyModel = buildMonthly(monthly, hoursMonthly, invoicedMonthly);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes jcSpin { to { transform: rotate(360deg); } }
        .j-refresh:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .j-note:hover { background: var(--accent-soft) !important; color: var(--accent-text) !important; }
        .j-note-ta:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--accent-ring) !important; }
      `}</style>

      {/* Export (PSR #14): the Project Status Report as a PDF report or an Excel workbook. */}
      <HeaderStrip fin={fin} onRefresh={loadData} exportProject={project} />
      <KpiCards fin={fin} project={project} onOpenSov={onOpenSov} phases={phases} collections={collections} hours={hours} endDate={endDate} onSeeAllPhases={() => phasesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '2px 2px 10px' }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Monthly Cost Trend</span>
          <MonthlyLegend monthly={monthlyModel} />
        </div>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-sm)', padding: '14px 16px' }}>
          <MonthlyChart monthly={monthlyModel} />
        </div>
        <MonthlyNote monthly={monthlyModel} />
      </div>

      <div ref={phasesRef}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '2px 2px 10px' }}>Phase Breakdown</span>
        {phases.length > 0 && shownPhases.length === 0
          ? <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12 }}>{noMatchText(search).body}</div>
          : <PhaseTable phases={shownPhases} jobId={ceJobId} getNotes={getNotes} addNote={addNote} />}
      </div>

      {summary.report_date && (
        <div className="mono" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 2px 4px', fontSize: 12.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>
          <span style={{ display: 'inline-flex', color: 'var(--text-4)' }}><JcIcon name="sync" size={12} /></span>
          Data as of {fmtDate(summary.report_date)}
          {summary.period_to && ` · Through ${summary.period_to}`}
        </div>
      )}
    </div>
  );
};

export default JobCostTab;
